import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Select, SelectChangeEvent, useTheme } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';

interface CustomSelectProps<T> {
    data: Array<T>;
    placeholder: string;
    currentValueId: string | undefined;
    setCurrentValue: (value: T) => void;
    config: {
        minWidth: number;
        height: number;
    };
}

export function CustomSelect<T extends { id: string; value: string }>({
    data,
    placeholder,
    currentValueId,
    setCurrentValue,
    config,
}: CustomSelectProps<T>) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);

    const handleChange = (event: SelectChangeEvent<string>) => {
        const selectedValue = data?.find((item) => item.id === event.target.value);
        if (selectedValue) {
            setCurrentValue(selectedValue);
        }
    };

    return (
        <Select
            sx={{
                height: config.height,
                minWidth: config.minWidth,
                backgroundColor: open
                    ? theme.palette.customColors.white
                    : theme.palette.customColors.lightOrange,
                borderRadius: theme.borderRadius.semiLarge,
                borderBottomRightRadius: 0,
                fontWeight: 700,
                fontSize: '0.875rem',
                border: `${theme.borderWidth.thin} solid ${theme.palette.customColors.lightOrange}`,
                '& .MuiSelect-icon': {
                    color: theme.palette.customColors.black,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                },
            }}
            IconComponent={KeyboardArrowDownIcon}
            MenuProps={{
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                },
                transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                },
                PaperProps: {
                    sx: {
                        ml: theme.spacing(2),
                        boxShadow: theme.customShadows.selectDropdown,
                        '& .MuiMenu-list': {
                            py: 0,
                        },
                    },
                },
            }}
            displayEmpty
            renderValue={(selectedValueId) => {
                if (!selectedValueId) {
                    return <span>{placeholder}</span>;
                }
                const item = data.find(({ id }) => id === selectedValueId);
                return item?.value ?? '';
            }}
            value={currentValueId ?? ''}
            onChange={handleChange}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            open={open}
        >
            {data.map(({ id, value }) => (
                <MenuItem
                    key={id}
                    value={id}
                    sx={{
                        px: 3,
                        '&.Mui-selected': {
                            backgroundColor: theme.palette.customColors.lightOrange,
                            '&:hover': {
                                backgroundColor: theme.palette.customColors.lightOrange,
                            },
                            '&:focus': {
                                backgroundColor: theme.palette.customColors.lightOrange,
                            },
                        },
                    }}
                >
                    {value}
                </MenuItem>
            ))}
        </Select>
    );
}
